import { isBlank } from '@ember/utils';
import { get, computed } from '@ember/object';

import {
  isOsUpgradeAvailable as isOsUpgradeAvailableFn,
  isAppUpgradeAvailable as isAppUpgradeAvailableFn,
} from 'garaje/utils/upgrade-available';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const IPAD_LAST_SEEN_THRESHOLD = 11 * MINUTE;

/**
 * @returns {*}
 */
export default function (Class) {
  const DecoratedClass = class extends Class {
    get isIpadNotSeenRecently() {
      return !this.isIpadSeenRecently;
    }

    get isStatusWarn() {
      return this.isOsUpgradeAvailable || this.isAppUpgradeAvailable || this.isPrinterErrored;
    }

    get isStatusBad() {
      return this.isIpadNotSeenRecently || this.hasPrinterIssues;
    }

    @computed('isPrinterEnabled', 'model.printerStatus')
    get isPrinterDisconnected() {
      if (this.isPrinterEnabled) {
        const status = get(this, 'model.printerStatus');
        return isBlank(status) || status === 'not connected';
      } else {
        return false;
      }
    }

    @computed('isPrinterEnabled', 'model.printerStatus')
    get isPrinterErrored() {
      if (this.isPrinterEnabled) {
        return get(this, 'model.printerStatus') !== 'connected';
      } else {
        return false;
      }
    }

    @computed('isPrinterEnabled', 'model.printerStatus')
    get hasPrinterIssues() {
      const status = get(this, 'model.printerStatus');

      if (this.isPrinterEnabled && status) {
        return status.match(/paper/);
      } else {
        return false;
      }
    }

    @computed('appVersion', 'currentAppVersion', 'model.{appVersion,currentIpadAppVersion}')
    get isAppUpgradeAvailable() {
      const appVersion = get(this, 'model.appVersion') ?? this.appVersion;
      const currentAppVersion = this.currentAppVersion ?? this.currentIpadAppVersion;
      return isAppUpgradeAvailableFn(appVersion, currentAppVersion);
    }

    @computed('maximumOsVersion', 'model.{maximumOsVersion,osVersion}', 'osVersion')
    get isOsUpgradeAvailable() {
      const osVersion = get(this, 'model.osVersion') ?? this.osVersion;
      const maximumOsVersion = get(this, 'model.maximumOsVersion') ?? this.maximumOsVersion;
      return isOsUpgradeAvailableFn(osVersion, maximumOsVersion);
    }

    // Native getter (not @computed) — autotracking reads @tracked properties
    // from DeviceStatusService when the feature flag is on.
    get isIpadSeenRecently() {
      if (this.locationFeatureFlags?.isEnabled('devices-service-status-api')) {
        const deviceId = get(this, 'model.id') ?? this.id;
        const status = this.deviceStatusService?.getStatus(deviceId);
        if (status) {
          return status.connectionStatus === 'CONNECTED';
        }
        // Fallback: device not in devices service yet — use legacy path
      }

      // Legacy path: threshold-based computation
      const heartbeat = get(this, 'model.lastHeartbeatAt') ?? this.lastHeartbeatAt;
      const now = new Date();
      const elapsed = now - heartbeat;
      return elapsed <= IPAD_LAST_SEEN_THRESHOLD;
    }

    get deviceStatus() {
      if (this.isStatusBad) {
        return 'bad';
      } else if (this.isStatusWarn) {
        return 'warn';
      } else {
        return 'good';
      }
    }
  };

  // reassign the original class name as the name of the decorated class
  Object.defineProperty(DecoratedClass, 'name', { value: Class.name });

  return DecoratedClass;
}

export { IPAD_LAST_SEEN_THRESHOLD };
